<template>
  <VueFinalModal
    modal-id="car-comparison-footer"
    content-class="car-comparison-footer"
    :lock-scroll="!largerThanMd && showCarsOnMobile"
    :hide-overlay="true"
    swipe-to-close="none"
    :click-to-close="false"
    background="interactive"
    content-transition="vfm-slide-down"
    overlay-transition="vfm-fade"
    @update:model-value="(val) => emit('update:modelValue', val)"
  >
    <CarComparisonFooterTopBand v-model:showCarsOnMobile="showCarsOnMobile" />

    <div class="container">
      <div class="car-comparison-footer__content">
        <div class="car-comparison-footer__car-tiles" v-show="showCarsOnMobile">
          <template v-if="!tabletViewport">
            <CarComparisonTile v-for="(num, index) in MAX_LIMIT" :key="num" :index="index" :vehicle-id="comparisonStore.selectedVehicleIds[index]" />
          </template>

          <Carousel v-else :items-to-show="itemsToShow" :wrap-around="true">
            <Slide v-for="(num, index) in Array(MAX_LIMIT).fill('')" :key="num">
              <div class="carousel__item">
                <CarComparisonTile :index="index" :vehicle-id="comparisonStore.selectedVehicleIds[index]" />
              </div>
            </Slide>

            <template #addons>
              <Pagination />
            </template>
          </Carousel>
        </div>

        <div class="car-comparison-footer__comparison-info">
          <h4 class="car-comparison-footer__comparison-text" v-text="comparisonText" />

          <a :href="comparisonPageUrl" class="btn btn-primary compare-cars-btn" v-text="comparisonFooter.viewComparisonButtonText" />
        </div>
      </div>
    </div>
  </VueFinalModal>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { VueFinalModal } from 'vue-final-modal';
import CarComparisonTile from './CarComparisonTile.vue';
import CarComparisonFooterTopBand from './CarComparisonFooterTopBand.vue';
import { Carousel, Pagination, Slide } from 'vue3-carousel';
import { useCarComparisonStore } from '../../foundation/stores/carComparisonStore';
import { useContentStore } from '../../foundation/stores/contentStore';
import { useBreakpoints } from '@vueuse/core';
import { useRouter } from 'vue-router';
import { useWindowSize } from '@vueuse/core';

import { APP_BREAKPOINTS } from '../../foundation/appConstants';
import { useUrlNavigator } from '../../composables/useUrlNavigator';

const emit = defineEmits<{
  (e: 'update:modelValue', modelValue: boolean): void;
}>();

// Constants
const MAX_LIMIT = 3;
const itemsToShow = ref(2);
const showCarsOnMobile = ref(false);

// Composables
const { width } = useWindowSize();
const router = useRouter();
const breakpoints = useBreakpoints(APP_BREAKPOINTS);
const contentStore = useContentStore();

// Pinia Store
const comparisonStore = useCarComparisonStore();

// Retrieve data from Sitecore and populate content store (unless already populated)
contentStore.fetchComparisonFooter();
const { comparisonFooter } = storeToRefs(contentStore);
const { getComparisonPageUrl } = useUrlNavigator(router);

// Vue 3 refs and reactives
const tabletViewport = breakpoints.between('md', 'xxl');
const largerThanMd = breakpoints.greaterOrEqual('md'); // only larger than sm

// Computed
const comparisonPageUrl = computed(() => {
  return getComparisonPageUrl(comparisonStore.selectedVehicleIds);
});

const comparisonText = computed(() => {
  return `${contentStore.comparisonFooter.comparisonTextPrefix} ${comparisonStore.vehicleIdsInCart.length}/${MAX_LIMIT} ${contentStore.comparisonFooter.comparisonTextSuffix}`;
});

// Watchers
watch(
  largerThanMd,
  () => {
    if (largerThanMd.value) {
      showCarsOnMobile.value = true;
    } else {
      showCarsOnMobile.value = false;
    }
  },
  { immediate: true }
);

watch(
  width,
  (val) => {
    const carouselItemsWidth = 600;
    return (itemsToShow.value = val / carouselItemsWidth);
  },
  { immediate: true }
);
</script>

<style lang="scss">
/* Note: These styles are not scoped as they are used on the vue-final-modal component */
.car-comparison-footer {
  position: absolute;
  bottom: 0;
  width: 100vw;
  background: #fff;
}

// ------ carousel ------ //

.carousel {
  width: 100%;
}

.carousel__item {
  min-height: auto;
  width: 100%;
  background-color: transparent;
  color: inherit;
  font-size: inherit;
  border-radius: 0;
  display: inherit;
  justify-content: center;
  align-items: center;
  text-align: left;
}

.carousel__slide {
  padding: 0;
}

.carousel__pagination-item {
  .carousel__pagination-button {
    border: 1px solid $racq-light-grey;
    background: #fff;
    width: 1rem;
    height: 1rem;
    border-radius: 50%;
    margin: 0 0.25rem;
    transition: 0.3s ease;
    outline: none;

    &--active {
      background: $racq-light-grey;
      width: 2rem;
      border-radius: 1.25rem;
    }

    &::after {
      background: transparent;
    }
  }

  .carousel__pagination-item .carousel__pagination-button--active {
    &::after {
      background: transparent;
    }
  }
}
</style>

<style lang="scss" scoped>
.car-comparison-footer {
  &__comparison-text {
    margin: 0;
    color: $racq-navy;
    font-size: 1rem;
    font-weight: bold;
    font-family: $firasans-regular-font;
  }

  &__comparison-info {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
    max-width: 100%;
    padding: 0 0.5rem;

    @media screen and (min-width: #{$breakpoint-md}) {
      flex-direction: column;
      align-items: center;
      justify-content: flex-start;
      min-width: 180px;
      padding: 0;
    }
  }

  &__content {
    padding: 1rem 0;
    display: flex;
    flex-direction: column;
    gap: 1rem;

    @media screen and (min-width: #{$breakpoint-md}) {
      flex-direction: row;
      justify-content: space-between;
      padding: 1rem 0.5rem;
      gap: 2rem;
    }

    @media screen and (min-width: #{$breakpoint-xl}) {
      padding: 1rem 0;
    }
  }

  &__car-tiles {
    overflow-y: hidden;
    padding: 0 0.5rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;

    @media screen and (min-width: #{$breakpoint-md}) {
      overflow-x: auto;
      padding: 0 1rem;
      flex-direction: row;
    }

    @media screen and (min-width: #{$breakpoint-xl}) {
      width: 100%;
      padding: 0;
    }
  }

  .compare-cars-btn {
    text-decoration: none;
    font-size: 1rem;
    max-width: 100%;
    padding: 1rem;
    height: auto;
    line-height: 1.25;

    @media screen and (min-width: #{$breakpoint-md}) {
      min-width: 10.5rem;
    }
  }
}
</style>
